import type { ChatObjectType } from "@/app/chatState";
import type { ChatMessage, ConversationBlock } from "@/app/typing/chat";

export type ObjectKey = { type: ChatObjectType; id: number };

/**
 * `messageId` orders selections within one conversation: the id of the newest persisted
 * message when the selection was made. History messages carry no timestamp, so ids are
 * the only ordering shared by the agent's picks and the user's.
 */
export type ConversationSelection = {
    selectedKeys: ObjectKey[];
    openKey: ObjectKey | null;
    messageId: number;
};

const OBJECT_TYPES = new Set<ChatObjectType>(["artifact", "event", "tag", "document"]);

/** Accepts both tool-input `{object_type, object_id}` and UI `{type, id}` shapes. */
export const objectKeysFromRefs = (objects: unknown): ObjectKey[] => {
    if (!Array.isArray(objects)) return [];
    const keys: ObjectKey[] = [];
    for (const raw of objects) {
        if (!raw || typeof raw !== "object") continue;
        const rec = raw as Record<string, unknown>;
        const typeVal = rec.object_type ?? rec.type;
        const idVal = rec.object_id ?? rec.id;
        if (typeof typeVal !== "string" || typeof idVal !== "number") continue;
        const type = typeVal.toLowerCase() as ChatObjectType;
        if (!OBJECT_TYPES.has(type)) continue;
        keys.push({ type, id: idVal });
    }
    return keys;
};

export const latestMessageId = (blocks: ConversationBlock[]): number => {
    let latest = 0;
    for (const block of blocks) {
        for (const message of block.content) {
            if (!message.incomplete && typeof message.id === "number" && message.id > latest) latest = message.id;
        }
    }
    return latest;
};

const selectionFromToolUse = (message: ChatMessage): ConversationSelection | null => {
    if (!Array.isArray(message.content) || typeof message.id !== "number") return null;
    for (let i = message.content.length - 1; i >= 0; i--) {
        const item = message.content[i];
        if (item.type !== "tool_use" || item.name !== "set_selected_objects_tool") continue;
        const keys = objectKeysFromRefs(item.input?.objects);
        if (keys.length === 0) continue;
        return { selectedKeys: keys, openKey: keys[0], messageId: message.id };
    }
    return null;
};

/** The agent's latest `set_selected_objects_tool` call in the thread. */
export const selectionFromHistory = (blocks: ConversationBlock[]): ConversationSelection | null => {
    for (let i = blocks.length - 1; i >= 0; i--) {
        const messages = blocks[i].content;
        for (let j = messages.length - 1; j >= 0; j--) {
            const found = selectionFromToolUse(messages[j]);
            if (found) return found;
        }
    }
    return null;
};

/** Objects the user deleted in the thread, from its `user_action` delete messages. */
export const deletedKeysFromHistory = (blocks: ConversationBlock[]): ObjectKey[] => {
    const deletes: unknown[] = [];
    for (const block of blocks) {
        for (const message of block.content) {
            if (!Array.isArray(message.content)) continue;
            for (const item of message.content as { type?: string; action?: string }[]) {
                if (item?.type === "user_action" && item.action === "delete") deletes.push(item);
            }
        }
    }
    return objectKeysFromRefs(deletes);
};

/** `selection` without `removed`; a removed open key falls back to the first remaining key. */
export const withoutKeys = (selection: ConversationSelection | null, removed: ObjectKey[]): ConversationSelection | null => {
    if (!selection || removed.length === 0) return selection;
    const gone = (key: ObjectKey) => removed.some((r) => r.type === key.type && r.id === key.id);
    const selectedKeys = selection.selectedKeys.filter((key) => !gone(key));
    const openKey = selection.openKey && !gone(selection.openKey) ? selection.openKey : (selectedKeys[0] ?? null);
    return { ...selection, selectedKeys, openKey };
};

/** A remembered selection holds unless the agent picked again after it was made. */
export const resolveSelection = (
    remembered: ConversationSelection | null | undefined,
    fromHistory: ConversationSelection | null | undefined,
): ConversationSelection | null => {
    if (!remembered) return fromHistory ?? null;
    if (!fromHistory) return remembered;
    return fromHistory.messageId > remembered.messageId ? fromHistory : remembered;
};
